import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from "react";
import Link from "next/link";
import { cn } from "@rosette/ts/src/util/cn.js";


/**
 * accent is the one action we want taken, such as Play or Subscribe.
 * primary is an alternative to it, neutral is everything else, and
 * danger ends or deletes something. success signals that something is
 * waiting on the player, such as an opponent's rematch request. rosette
 * is the account pages' action, in the Rosette Games blue.
 */
export type ButtonTone = "accent" | "primary" | "neutral" | "danger" | "success" | "rosette";

/** sm is for buttons inside list rows. */
export type ButtonSize = "sm" | "md" | "lg";

// The coloured tones lighten their label on hover, as the old buttons did.
// primary's must, or the premium sheen's navy hover band would vanish.
const TONE_CLASSES: Record<ButtonTone, string> = {
    accent: "bg-accent text-on-accent hover:text-on-accent-hover",
    primary: "bg-primary text-on-primary hover:text-on-primary-hover",
    neutral: "bg-neutral text-on-light",
    danger: "bg-danger text-on-danger hover:text-on-danger-hover",
    success: "bg-success text-on-success",
    rosette: "bg-rosette-strong text-strong",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
    sm: "px-4 py-2 text-base shadow-sm",
    md: "px-4 py-3 text-xl shadow-sm",
    lg: "px-9 py-3 text-2xl shadow-md",
};


interface ButtonStyleProps {
    tone?: ButtonTone;
    size?: ButtonSize;
}

interface LinkButtonProps extends ButtonStyleProps, AnchorHTMLAttributes<HTMLAnchorElement> {
    href: string;
    /** false for a link to a route handler, which a prefetch would run. */
    prefetch?: boolean;
}

interface NativeButtonProps extends ButtonStyleProps, ButtonHTMLAttributes<HTMLButtonElement> {
    href?: undefined;
}

type ButtonProps = LinkButtonProps | NativeButtonProps;


/**
 * A raised button with a thick bottom edge. Given an href it renders a
 * Next.js Link that looks the same.
 */
export function Button({ tone = "neutral", size = "md", className, ...props }: ButtonProps) {
    const classes = cn(
        "inline-block cursor-pointer select-none rounded-lg border-b-4 border-b-shade bg-none",
        "text-center font-bold no-underline hover:no-underline",
        "hover:brightness-110 active:brightness-90",
        // Disabled is the same flat, translucent look in every tone. The bottom
        // edge becomes a transparent 2px border top and bottom, so the height
        // and the label's centre do not move.
        "disabled:cursor-not-allowed disabled:brightness-100 disabled:shadow-none",
        "disabled:border-y-2 disabled:border-transparent disabled:bg-chip-strong/10 disabled:text-strong/45",
        TONE_CLASSES[tone],
        SIZE_CLASSES[size],
        className ?? null,
    );

    if (props.href !== undefined)
        return <Link {...props} className={classes} />;

    return <button type="button" {...props} className={classes} />;
}
